import {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import {getEmailData} from '../../../redux/branding/brandingActions';
import {getEventSettings} from '../../../redux/event/eventActions';
import {getPackageProducer} from '../../../redux/account/accountActions';
import {hasAccountAccess} from '../../../functions/permission';
import {setCurrentRating} from '../../components/rating/Rating';
import setGlobalState from '../../../redux/setGlobalState';
import useShow from '../../../hooks/useShow';
import useTablet from '../../../hooks/useTablet';
import {createScreensTypes, MESSAGE_TYPES, DELIVERY_TYPES} from '../menu/brandingConfig';
import {buildBrandingPath, getBrandingPreviewProps, resolveDeliveryType} from '../menu/brandingRouteHelper';
import type {DeliveryType, MessageType} from '../../../hooks/useBrandingParams';
import type {BrandingContextValue} from '../context/BrandingContext';
import type {TState} from '../../../redux/store.type';

export function useBrandingPage() {
    const params = useParams();
    const navigate = useNavigate();
    const account = useSelector((s: TState) => s.account);
    const event = useSelector((s: TState) => s.event);
    const {currentLang, getBrandingData, updateDesignEmailXHR} = useSelector((s: TState) => s.branding);
    const isTablet = useTablet();
    const isKeyboardOpen = useSelector((s: TState) => s.ui.isKeyboardOpen);

    const messageType = (params.messageType || MESSAGE_TYPES.sendTicket) as MessageType;
    const deliveryType = (params.deliveryType || DELIVERY_TYPES.email) as DeliveryType;

    const [currentEditorName, setCurrentEditorName] = useState<string | undefined>();
    const [message, setMessage] = useState<string>('');
    const aiGeneratorShow = useShow();

    const eventWhatsAppStatus = hasAccountAccess('whatsApp') && event.settings?.ticketsMailing?.whatsAppMessage;
    const resolvedDeliveryType = resolveDeliveryType(messageType, deliveryType);

    useEffect(() => {
        if(resolvedDeliveryType !== deliveryType) {
            navigate(buildBrandingPath(event.eid, event.type, messageType, resolvedDeliveryType), {replace: true});
        }
    }, [messageType, deliveryType, resolvedDeliveryType, event.eid, event.type, navigate]);

    useEffect(() => {
        if(!event.getEventSettingsXHR) {
            getEventSettings().then();
        }
        if(!currentLang && event.lang) {
            setGlobalState('branding.currentLang', event.lang);
        }
        return () => {
            if(updateDesignEmailXHR === 'SUCCESS') {
                setCurrentRating('brandingTicket');
            }
        };
    }, []);

    useEffect(() => {
        if(!getBrandingData?.[`${currentLang}XHR`]) {
            getEmailData().then();
        }
    }, [currentLang]);

    useEffect(() => {
        if(account.pid && ['SUCCESS', 'UPDATED'].includes(account.getAccountXHR)) {
            getPackageProducer().then();
        }
    }, [account.getAccountXHR]);

    const screensTypes = useMemo(
        () => createScreensTypes(eventWhatsAppStatus, account.packagesProducer),
        [eventWhatsAppStatus, account.packagesProducer],
    );

    const {Comps, CompPreview, CompPreviewProps} = screensTypes[deliveryType] || screensTypes.email;
    const Comp = Comps[messageType];
    const PreviewComponent = deliveryType === DELIVERY_TYPES.email
        ? CompPreview[messageType]
        : CompPreview;

    const previewProps = getBrandingPreviewProps(
        deliveryType,
        CompPreviewProps as Record<string, unknown> | undefined,
        messageType,
        event.type,
        event.eid,
    );

    const contextValue: BrandingContextValue = {
        textEditor: {currentEditorName, setCurrentEditorName},
        ...(deliveryType === DELIVERY_TYPES.whatsapp && {
            whatsapp: {message, setMessage, aiGeneratorShow},
        }),
    };

    return {
        isLoading: event.getEventSettingsXHR === 'LOADING',
        messageType,
        deliveryType,
        eventWhatsAppStatus,
        eventEid: event.eid,
        aiGeneratorShow,
        contextValue,
        isTablet,
        isKeyboardOpen,
        Comp,
        PreviewComponent,
        previewProps,
        CompPreviewProps,
    };
}
